import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Download, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Api from "@/data/api";

export function PublicDownloadScreen() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobData, setJobData] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchJobData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const api = Api.getApiWithoutToken();
        const response = await api.get(`download/${token}`);
        setJobData(response.data);
      } catch (err) {
        console.error("Failed to fetch job data:", err);
        setError(
          err.response?.data?.message || 
          "Failed to load download page. The link may be invalid or expired."
        );
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchJobData();
    }
  }, [token]);

  const handleDownload = () => {
    try {
      setDownloading(true);
      // Direct download via backend URL
      const baseUrl = Api.getBaseUrl();
      window.location.href = `${baseUrl}download/${token}/image`;
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      // Reset after a short delay since redirect happens
      setTimeout(() => setDownloading(false), 1000);
    }
  };

  // Custom branding styles
  const brandingStyles = jobData?.branding ? {
    primaryColor: jobData.branding.primaryColor || '#3b82f6',
    secondaryColor: jobData.branding.secondaryColor || '#1e40af',
  } : {
    primaryColor: '#3b82f6',
    secondaryColor: '#1e40af',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="text-lg text-gray-600 dark:text-gray-300">Loading your photo...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-6 w-6" />
              Download Not Available
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
              <p className="text-red-800 dark:text-red-200">{error}</p>
            </div>
            <Button asChild className="w-full">
              <Link to="/">Go to Home</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4"
      style={{
        background: `linear-gradient(to bottom, ${brandingStyles.primaryColor}10, ${brandingStyles.secondaryColor}10)`
      }}
    >
      <div className="w-full max-w-4xl">
        {/* Branding Logo */}
        {jobData?.branding?.logo && (
          <div className="text-center mb-6">
            <img 
              src={jobData.branding.logo} 
              alt="Brand Logo" 
              className="h-16 mx-auto object-contain"
            />
          </div>
        )}

        <Card className="overflow-hidden">
          <CardHeader className="text-center" style={{ borderTopColor: brandingStyles.primaryColor }}>
            <div className="flex items-center justify-center gap-2 mb-2">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <CardTitle className="text-2xl">Your Photo is Ready!</CardTitle>
            <p className="text-gray-600 dark:text-gray-300">
              Template: {jobData.templateName}
            </p>
          </CardHeader>

          <CardContent className="p-6">
            {/* Image Preview */}
            <div className="mb-6 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 shadow-lg">
              <img 
                src={jobData.outputImageUrl} 
                alt="Your AI-enhanced photo" 
                className="w-full h-auto object-contain max-h-[600px]"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect fill="%23ddd" width="400" height="300"/><text x="50%" y="50%" text-anchor="middle" fill="%23999">Image not available</text></svg>';
                }}
              />
            </div>

            {/* Download Button */}
            <div className="space-y-4">
              <Button 
                onClick={handleDownload}
                disabled={downloading}
                className="w-full"
                size="lg"
                style={{ backgroundColor: brandingStyles.primaryColor }}
              >
                {downloading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-5 w-5" />
                    Download Full Resolution
                  </>
                )}
              </Button>

              {/* Info */}
              <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                <p>Created on {new Date(jobData.createdAt).toLocaleDateString()}</p>
                <p className="mt-1">This link will expire in 7 days</p>
              </div>
            </div>

            {/* Additional Info */}
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Want to create your own AI-enhanced photos?
              </p>
              <Button 
                asChild 
                variant="outline" 
                className="mt-2"
                style={{ borderColor: brandingStyles.primaryColor, color: brandingStyles.primaryColor }}
              >
                <Link to="/">Visit our website</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

